import React from 'react';
import { MessageSquare, Lightbulb, AlertCircle, Plus, Calendar, ChevronRight, Clock, CheckCircle2, ArrowRight, Zap, Sparkles, Trophy, Lock, TrendingUp } from 'lucide-react';

export default function CollegeJourneyHub() {
  return (
    <div className="min-h-screen bg-stone-50" style={{ fontFamily: "'Reddit Sans', system-ui, sans-serif" }}>
      {/* Navigation */}
      <nav className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-8 text-sm">
              <button className="text-slate-900 font-medium border-b-2 border-slate-900 pb-4">Hub</button>
              <button className="text-stone-600 hover:text-slate-900 pb-4">Profile</button>
              <button className="text-stone-600 hover:text-slate-900 pb-4">Colleges</button>
              <button className="text-stone-600 hover:text-slate-900 pb-4">Essays</button>
              <button className="text-stone-600 hover:text-slate-900 pb-4">Timeline</button>
              <button className="text-stone-600 hover:text-slate-900 pb-4">Recommendations</button>
              <button className="text-stone-600 hover:text-slate-900 pb-4">Aid</button>
              <button className="text-stone-600 hover:text-slate-900 pb-4">Compare</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold mb-2">College Journey</h1>
              <p className="text-stone-300 text-sm">Wednesday, October 9, 2025 • 22 days until Early Action deadline</p>
            </div>
            <div className="text-right">
              <div className="text-xs text-stone-400 mb-2">Application Readiness</div>
              <div className="flex items-center gap-3">
                <div className="w-32 h-2 bg-slate-800 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-pink-500 via-orange-500 to-violet-500" style={{ width: '65%' }}></div>
                </div>
                <span className="text-2xl font-bold text-orange-400">65%</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <button className="px-4 py-2 bg-slate-900 text-white rounded-lg text-sm font-medium">
                Focused View
              </button>
              <button className="px-4 py-2 text-slate-700 hover:bg-stone-100 rounded-lg text-sm">
                Overview
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium border-2 border-slate-900 hover:bg-teal-700 transition-colors flex items-center gap-2">
                <Plus className="w-4 h-4" />
                Add Task
              </button>
              <button className="px-4 py-2 text-slate-700 border border-stone-300 hover:bg-stone-50 rounded-lg text-sm flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Timeline
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
          {/* Main Column */}
          <div className="space-y-8">
            
            {/* PRIMARY FOCUS - Hero Treatment */}
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Zap className="w-5 h-5 text-orange-600" />
                <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Your Best Move Right Now</h2>
              </div>
              
              <div className="bg-white border-2 border-slate-900 rounded-xl p-8 shadow-sm">
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center flex-shrink-0 border-2 border-slate-900">
                    <Lightbulb className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-slate-900 text-lg mb-2">Complete your Common App essay</h3>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="flex items-center gap-2 text-sm text-stone-700">
                        <ArrowRight className="w-4 h-4 text-orange-600" />
                        <span>Unlocks <strong>4 schools</strong></span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-stone-700">
                        <Clock className="w-4 h-4 text-orange-600" />
                        <span>~3.5 hours</span>
                      </div>
                    </div>

                    <p className="text-sm text-stone-700 mb-5 leading-relaxed">
                      This is your foundation for all supplemental essays and enables you to submit 4 Early Action applications. Everything else depends on this.
                    </p>

                    <div className="bg-orange-50 rounded-lg p-4 mb-5 border border-orange-200">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-stone-800">
                          <span className="font-medium text-orange-900">Ready to start:</span> Your brainstorming notes, 3 strong opening hooks from your leadership experience, and 650-word outline are prepared
                        </div>
                      </div>
                    </div>

                    <button className="px-6 py-3 bg-orange-500 text-white rounded-lg text-sm font-bold border-2 border-slate-900 hover:bg-orange-600 transition-all hover:scale-[1.02] flex items-center gap-2">
                      Start writing
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* INLINE NOTIFICATIONS - Horizontal Banner Style */}
            <div className="space-y-3">
              
              {/* Parent Comment - Compact Inline */}
              <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-lg p-4 flex items-center justify-between hover:bg-blue-100 transition-colors">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <MessageSquare className="w-5 h-5 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900 truncate">Your mom commented on UC Essay Draft 2</p>
                    <p className="text-xs text-stone-600">2 hours ago</p>
                  </div>
                </div>
                <button className="text-blue-600 text-sm font-medium hover:text-blue-700 flex items-center gap-1 flex-shrink-0">
                  View
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Feature Unlock - Inline with Icon */}
              <div className="bg-violet-50 border-l-4 border-violet-500 rounded-r-lg p-4 flex items-center justify-between hover:bg-violet-100 transition-colors">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-violet-500 flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-900">Portfolio Strategy tool now available</p>
                    <p className="text-xs text-stone-600">Based on your 12 colleges</p>
                  </div>
                </div>
                <button className="text-violet-600 text-sm font-medium hover:text-violet-700 flex items-center gap-1 flex-shrink-0">
                  Open
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>

            </div>

            {/* NEEDS ATTENTION - Grouped with Visual Break */}
            <div>
              {/* Section Header with Alert */}
              <div className="flex items-center gap-3 mb-4 pb-3 border-b-2 border-coral-200">
                <AlertCircle className="w-5 h-5 text-coral-600" />
                <div className="flex-1">
                  <h2 className="text-sm font-bold text-slate-900 uppercase tracking-wide">Needs Attention</h2>
                  <p className="text-xs text-stone-600 mt-0.5">3 items with approaching deadlines</p>
                </div>
              </div>

              <div className="space-y-4">
                
                {/* Dense Task Cards - Different Layout */}
                <div className="bg-white border-l-4 border-coral-500 rounded-r-lg p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-bold text-slate-900 text-sm">Request recommendation from Ms. Chen</h4>
                        <span className="text-xs px-2 py-0.5 bg-coral-100 text-coral-700 rounded font-medium">6d overdue</span>
                      </div>
                      <p className="text-xs text-stone-600 mb-3">
                        Teachers need 3-4 weeks notice. Ms. Chen travels Nov 1-15. Draft email highlighting your AP Chem project is ready.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-stone-500">~2 min to review & send</span>
                    <button className="px-4 py-2 bg-coral-600 text-white rounded-lg text-xs font-bold border border-slate-900 hover:bg-coral-700 transition-colors">
                      Review draft
                    </button>
                  </div>
                </div>

                <div className="bg-white border-l-4 border-orange-400 rounded-r-lg p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-bold text-slate-900 text-sm">Submit SAT scores to Stanford</h4>
                        <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded font-medium">Due Oct 15</span>
                      </div>
                      <p className="text-xs text-stone-600 mb-3">
                        Score delivery takes 1-2 weeks. Your Oct SAT scores are available.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-stone-500">~5 min</span>
                    <button className="px-4 py-2 bg-white text-slate-900 rounded-lg text-xs font-bold border border-slate-900 hover:bg-stone-50 transition-colors">
                      Go to College Board
                    </button>
                  </div>
                </div>

                <div className="bg-white border-l-4 border-orange-400 rounded-r-lg p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-bold text-slate-900 text-sm">Complete financial aid pre-check</h4>
                        <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded font-medium">Due Oct 12</span>
                      </div>
                      <p className="text-xs text-stone-600 mb-3">
                        Required before FAFSA submission. Gather parent tax documents.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-stone-500">~20 min</span>
                    <button className="px-4 py-2 bg-white text-slate-900 rounded-lg text-xs font-bold border border-slate-900 hover:bg-stone-50 transition-colors">
                      Start pre-check
                    </button>
                  </div>
                </div>

              </div>
            </div>

            {/* READY QUEUE - Minimal List Style */}
            <div>
              <div className="flex items-center justify-between mb-4 pb-2 border-b border-stone-200">
                <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wide">Ready When You Are</h2>
                <span className="text-xs text-stone-500">4 tasks ready</span>
              </div>

              <div className="space-y-2">
                
                <div className="group flex items-center gap-3 p-3 hover:bg-white rounded-lg transition-colors cursor-pointer border border-transparent hover:border-stone-200">
                  <div className="w-4 h-4 rounded border-2 border-stone-400 flex-shrink-0"></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 group-hover:text-slate-700">Finalize activities list</p>
                    <p className="text-xs text-stone-600">Pre-filled from brag sheet — trim to 150 chars</p>
                  </div>
                  <span className="text-xs text-stone-500 flex-shrink-0">~15 min</span>
                </div>

                <div className="group flex items-center gap-3 p-3 hover:bg-white rounded-lg transition-colors cursor-pointer border border-transparent hover:border-stone-200">
                  <div className="w-4 h-4 rounded border-2 border-stone-400 flex-shrink-0"></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 group-hover:text-slate-700">Review Georgetown requirements</p>
                    <p className="text-xs text-stone-600">Check interview policy and essay requirements</p>
                  </div>
                  <span className="text-xs text-stone-500 flex-shrink-0">~10 min</span>
                </div>

                <button className="w-full text-xs text-stone-600 hover:text-slate-900 py-3 flex items-center justify-center gap-1 border-t border-stone-200 mt-2">
                  Show 2 more ready tasks
                  <ChevronRight className="w-3 h-3" />
                </button>

              </div>
            </div>

          </div>

          {/* Smart Sidebar - Varied Layouts */}
          <div className="space-y-6 lg:sticky lg:top-8 lg:self-start">
            
            {/* This Week - Dense Info Block */}
            <div className="bg-slate-900 text-white rounded-xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xs font-bold uppercase tracking-wide">This Week</h3>
                <span className="text-xs text-stone-400">Oct 9-15</span>
              </div>
              
              <div className="mb-5 p-3 bg-orange-500/20 border border-orange-400/30 rounded-lg">
                <div className="text-xs font-medium text-orange-200 mb-1">Key milestone</div>
                <div className="text-sm font-bold text-white">Common App essay</div>
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-coral-400"></div>
                    <span className="text-stone-300">Needs attention</span>
                  </div>
                  <span className="font-bold">3</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-orange-400"></div>
                    <span className="text-stone-300">Critical path</span>
                  </div>
                  <span className="font-bold">1</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-stone-500"></div>
                    <span className="text-stone-300">Ready to work</span>
                  </div>
                  <span className="font-bold">4</span>
                </div>
              </div>

              <div className="mt-5 pt-5 border-t border-stone-700">
                <div className="text-xs text-stone-400 mb-2">Time needed</div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-coral-400 via-orange-400 to-stone-500" style={{ width: '55%' }}></div>
                  </div>
                  <span className="text-lg font-bold">7.5h</span>
                </div>
                <div className="text-xs text-stone-500 mt-1">of ~14h available</div>
              </div>
            </div>

            {/* Strategy - Compact Stats */}
            <div className="bg-white border border-stone-300 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-pink-600" />
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wide">Strategy</h3>
              </div>
              
              <div className="space-y-3 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-stone-600">Application approach</span>
                  <span className="font-bold text-slate-900">EA (4)</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-600">Essay strategy</span>
                  <span className="font-bold text-slate-900">Common → Adapt</span>
                </div>
                <div className="pt-2 border-t border-stone-200">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-stone-600">Timeline confidence</span>
                    <span className="font-bold text-pink-600">85%</span>
                  </div>
                  <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden">
                    <div className="h-full bg-pink-500 rounded-full" style={{ width: '85%' }}></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Stats - Ultra Minimal */}
            <div className="bg-white border border-stone-200 rounded-lg p-4">
              <h3 className="text-xs font-bold text-slate-900 mb-3 uppercase tracking-wide">Progress</h3>
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-stone-600">Essays</span>
                  <span className="font-mono font-medium text-slate-900">2/8</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-600">Recommendations</span>
                  <span className="font-mono font-medium text-slate-900">1/3</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-stone-600">Schools researched</span>
                  <span className="font-mono font-medium text-slate-900">6/12</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}